import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { formatNumber, titleCase } from '../../lib/format'
import type { Organization } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, Select, Skeleton, StatusBadge,
} from '../../components/ui'

interface LandParcel {
  id?: string
  name?: string
  region_id?: string
  district_id?: string
  area_size_ha?: number
  eligible_minerals?: string[]
  status?: string
  allocated_org_id?: string
  notes?: string
}

const LAND_ADMIN_ROLES = new Set(['super_admin', 'gov_director', 'gov_regional'])
const PARCEL_STATUSES = ['available', 'reserved', 'allocated', 'restricted', 'reclaimed']

export default function LandParcelDetail() {
  const { parcelId } = useParams<{ parcelId: string }>()
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [orgToAllocate, setOrgToAllocate] = useState('')

  const parcelQuery = useQuery({
    queryKey: ['gov', 'parcel', parcelId],
    queryFn: () => api.get<{ parcel: LandParcel }>(`/parcels/${parcelId}`),
    enabled: Boolean(parcelId),
  })
  const orgsQuery = useQuery({
    queryKey: ['gov', 'orgs-for-parcel'],
    queryFn: () => api.get<{ organizations: Organization[] }>('/organizations?limit=300'),
  })

  const allocate = useMutation({
    mutationFn: (org_id: string) => api.post(`/gov/parcels/${parcelId}/allocate`, { org_id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'parcel', parcelId] })
      setOrgToAllocate('')
    },
  })
  const revoke = useMutation({
    mutationFn: (reason: string) => api.post(`/gov/parcels/${parcelId}/revoke`, { reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['gov', 'parcel', parcelId] }),
  })
  const setStatus = useMutation({
    mutationFn: (status: string) => api.post(`/gov/parcels/${parcelId}/status`, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['gov', 'parcel', parcelId] }),
  })

  if (!parcelId) return <Navigate to="/parcels" replace />
  if (parcelQuery.isError) {
    return (
      <Alert tone="danger">
        {(parcelQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/parcels">Back to parcels</Link></div>
      </Alert>
    )
  }
  if (parcelQuery.isLoading || !parcelQuery.data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const parcel = parcelQuery.data.parcel
  const canManage = LAND_ADMIN_ROLES.has(user?.role ?? '')
  const allocatedOrg = (orgsQuery.data?.organizations ?? []).find((o) => o.id === parcel.allocated_org_id)

  async function onRevoke(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const reason = String(new FormData(e.currentTarget).get('reason') ?? '').trim()
    if (await confirm({ title: 'Revoke this land allocation?', message: reason || undefined, destructive: true })) {
      revoke.mutate(reason)
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{parcel.name}</h1>
          <p className="page-head__sub">
            {[titleCase(parcel.district_id), titleCase(parcel.region_id)].filter(Boolean).join(', ') || 'Location not set'}
          </p>
        </div>
        <StatusBadge status={parcel.status || 'available'} />
      </div>

      {(allocate.isError || revoke.isError || setStatus.isError) && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">
            {[allocate.error, revoke.error, setStatus.error].find((e) => e instanceof ApiError) instanceof ApiError
              ? ((allocate.error ?? revoke.error ?? setStatus.error) as ApiError).message
              : 'That action failed.'}
          </Alert>
        </div>
      )}

      <div className="grid grid--2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <div className="stack">
              <Row label="Area" value={parcel.area_size_ha ? `${formatNumber(parcel.area_size_ha, 2)} ha` : '—'} />
              <Row label="Eligible minerals" value={(parcel.eligible_minerals ?? []).map(titleCase).join(', ') || '—'} />
              <Row label="Allocated to" value={allocatedOrg?.name || 'Not allocated'} />
            </div>
          </CardBody>
        </Card>
        {canManage && (
          <Card>
            <CardHeader title="Status" />
            <CardBody>
              <Select
                label="Change status"
                value={parcel.status || 'available'}
                onChange={(e) => setStatus.mutate(e.target.value)}
                disabled={setStatus.isPending}
              >
                {PARCEL_STATUSES.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
              </Select>
            </CardBody>
          </Card>
        )}
      </div>

      {canManage && (
        <Card>
          <CardHeader title="Allocation" />
          <CardBody>
            {parcel.allocated_org_id ? (
              <form onSubmit={onRevoke}>
                <div className="stack" style={{ gap: 'var(--sp-3)' }}>
                  <p style={{ margin: 0 }}>Currently allocated to <strong>{allocatedOrg?.name || parcel.allocated_org_id}</strong>.</p>
                  <Select label="Reason for revoking (optional)" name="reason">
                    <option value="">—</option>
                    <option value="Non-compliance">Non-compliance</option>
                    <option value="Allocation error">Allocation error</option>
                    <option value="Organisation request">Organisation request</option>
                  </Select>
                  <div className="row">
                    <Button type="submit" variant="danger" loading={revoke.isPending}>Revoke allocation</Button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-3)' }}>
                <div style={{ flex: 1 }}>
                  <Select label="Allocate to" value={orgToAllocate} onChange={(e) => setOrgToAllocate(e.target.value)}>
                    <option value="">Select an organisation…</option>
                    {(orgsQuery.data?.organizations ?? []).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </Select>
                </div>
                <Button
                  disabled={!orgToAllocate}
                  loading={allocate.isPending}
                  onClick={async () => {
                    const orgName = orgsQuery.data?.organizations.find((o) => o.id === orgToAllocate)?.name
                    if (await confirm({ title: `Allocate this parcel to ${orgName || 'this organisation'}?` })) {
                      allocate.mutate(orgToAllocate)
                    }
                  }}
                >
                  Allocate
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
